// Clears app auth/session-related localStorage keys without nuking unrelated keys for this origin.
import { OTP_DELIVERY_STORAGE_KEYS } from './otpDelivery';

// Storage keys organized by category for easier maintenance
const STORAGE_KEYS = {
  // Auth tokens - security critical, must clear on impersonation boundary
  auth: [
    'm8_access',
    'm8_client',
    'm8_chrome_ext_access',
    'm8_chrome_ext_client',
    'm8_chrome_ext_expiry',
  ],

  // Session/role flags
  session: ['flags', 'user_flags', 'is_admin', 'trial_user', 'login_provider'],

  // User profile data
  user: ['phone_number', 'customer_payment_id', 'tracking'],

  // How the last sign-in code was delivered (email, SMS, or authenticator app)
  otpDelivery: OTP_DELIVERY_STORAGE_KEYS,

  // Organization/department data
  organization: [
    'departments',
    'allDepartments',
    'allowed_user_domains',
    'default_fax_number',
    'associated_fax_numbers',
    'user_can_send_fax_records_only',
  ],

  // Referral/template data
  referral: ['referral_sms_template', 'referral_call_templates', 'referral_reply_templates'],
};

// Flatten all categorized keys into a single array
const ALL_EXPLICIT_KEYS = Object.values(STORAGE_KEYS).flat();

const CHROME_EXT_KEYS = new Set([
  'm8_chrome_ext_access',
  'm8_chrome_ext_client',
  'm8_chrome_ext_expiry',
]);

const PRESERVED_EMAIL_KEY = 'm8_email';

export default function clearM8LocalStorage(options = {}) {
  const {
    preserveChromeExtension = false,
    preserveEmail = false,
    // Opt-IN, like every other preserve in this module.
    //
    // This was an unconditional, hardcoded exception list living in `global/`, which
    // broke the module's contract in two ways: it stopped being the app's clear
    // authority (no caller — not a future account-switch, not a "clear all local
    // data" path — could wipe those keys), and it put feature knowledge two layers
    // from the feature that owns it. The key names now come from
    // components/tutorial/tutorialProgress.js and only logout asks for them.
    //
    // `m8_has_completed_walkthrough` is deliberately NOT in that set: it is a flat
    // global boolean, not namespaced per user, and it is rewritten from the server
    // payload on every login — so preserving it bought nothing and only widened the
    // window in which one account's walkthrough state was visible under another.
    preserveKeys = null,
  } = options;
  const preserved = preserveKeys instanceof Set
    ? preserveKeys
    : new Set(Array.isArray(preserveKeys) ? preserveKeys : []);
  const keysToRemove = [];

  // Collect all keys with m8_ prefix (catches any new keys automatically)
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (key.indexOf('m8_') === 0) keysToRemove.push(key);
  }

  // Add all explicitly defined keys (defense in depth for auth tokens)
  ALL_EXPLICIT_KEYS.forEach((key) => keysToRemove.push(key));

  // Deduplicate and remove
  const seen = {};
  keysToRemove.forEach((key) => {
    if (seen[key]) return;
    seen[key] = true;
    if (preserveChromeExtension && CHROME_EXT_KEYS.has(key)) return;
    if (preserveEmail && key === PRESERVED_EMAIL_KEY) return;
    if (preserved.has(key)) return;
    try {
      localStorage.removeItem(key);
    } catch {
      // localStorage can throw in private browsing mode, when disabled, or on quota errors.
      // Continue clearing other keys - partial cleanup is better than none.
    }
  });
}
